import React, { useState } from 'react';
import { useRouter } from 'expo-router';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { PlannedMeal } from '../../redux/mealPlanSlice';
import { abandonSession, setScreen } from '../../redux/cookingSessionSlice';
import { consumeItemsForCooking, DeductionSummary } from '../../redux/inventorySlice';
import { CookingStepScreen } from '../cooking/CookingStepScreen';

interface CookingViewProps {
  meal: PlannedMeal;
  userId?: string;
  onDone: (summary: DeductionSummary[]) => void;
}

/**
 * Meal-plan cooking path. All step UI lives in the shared CookingStepScreen;
 * this wrapper only owns the meal-plan finish flow (pantry deduction).
 */
const CookingView: React.FC<CookingViewProps> = ({ meal, userId, onDone }) => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const recipe = meal.recipe!;

  const [isFinishing, setIsFinishing] = useState(false);

  const handleQuit = () => {
    dispatch(abandonSession());
    router.push('/(app)');
  };

  const handleFinish = async () => {
    setIsFinishing(true);
    try {
      const result = await dispatch(
        consumeItemsForCooking({
          extendedIngredients: recipe.extendedIngredients ?? [],
          servings: meal.servings,
          plannedServings:
            typeof recipe.servings === 'number'
              ? recipe.servings
              : parseInt(String(recipe.servings)) || meal.servings,
          userId,
        })
      ).unwrap();
      onDone(result);
      dispatch(setScreen('done'));
    } catch (error) {
      console.error('Error consuming items:', error);
      setIsFinishing(false);
    }
  };

  return (
    <CookingStepScreen
      recipe={recipe}
      servingsOverride={meal.servings}
      finishDisabled={isFinishing}
      onQuit={handleQuit}
      onFinish={handleFinish}
    />
  );
};

export default CookingView;
